import {
    Box,
    ButtonBase,
    IconButton,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { displayCurrencyForPool, formatSignedCurrencyAmountOrMasked } from "features/wallet/lib/walletDisplay";
import type { WalletAccountIconModel } from "features/wallet/lib/walletDisplay";
import type { LiquidityPool } from "features/wallet/api/Wallet";

export interface AccountRowProps {
    pool: LiquidityPool;
    icon: WalletAccountIconModel;
    subtitle: string;
    locale: string;
    balancesVisible: boolean;
    onClick: () => void;
    onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
    openLabel: string;
    menuLabel: string;
    menuDisabled?: boolean;
    menuTitle?: string;
    showDivider: boolean;
    compact?: boolean;
}

export default function AccountRow({
    pool,
    icon,
    subtitle,
    locale,
    balancesVisible,
    onClick,
    onMenuClick,
    openLabel,
    menuLabel,
    menuDisabled,
    menuTitle,
    showDivider,
    compact = false,
}: AccountRowProps) {
    const { Icon } = icon;
    const displayCurrency = displayCurrencyForPool(pool.currency);
    const amountColor = pool.currentBalance < -0.01
        ? "error.main"
        : pool.currentBalance > 0.01 ? "success.main" : "text.secondary";

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) 36px",
                alignItems: "stretch",
                borderBottom: showDivider ? (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}` : 0,
            }}
        >
            <ButtonBase
                onClick={onClick}
                aria-label={openLabel}
                sx={{
                    minWidth: 0,
                    justifyContent: "stretch",
                    textAlign: "left",
                    p: compact ? 1 : { xs: 1.25, sm: 1.5 },
                    "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.05),
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: compact
                            ? "36px minmax(0, 1fr)"
                            : { xs: "42px minmax(0, 1fr)", sm: "48px minmax(0, 1fr)" },
                        gap: compact ? 1 : { xs: 1.15, sm: 1.35 },
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: compact ? 36 : { xs: 42, sm: 48 },
                            height: compact ? 36 : { xs: 42, sm: 48 },
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            color: icon.color,
                            bgcolor: icon.background,
                            fontWeight: 900,
                            fontSize: compact ? 18 : { xs: 20, sm: 22 },
                            boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.3)}`,
                            "& svg": { fontSize: compact ? 20 : { xs: 24, sm: 27 } },
                        }}
                    >
                        {Icon ? <Icon /> : icon.letter}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            noWrap
                            sx={{
                                color: "text.primary",
                                fontWeight: 900,
                                fontSize: compact ? 15 : { xs: 16, sm: 17 },
                                lineHeight: 1.15,
                            }}
                        >
                            {pool.name}
                        </Typography>
                        <Box
                            sx={{
                                mt: 0.35,
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: 1,
                                alignItems: "baseline",
                            }}
                        >
                            <Typography noWrap variant="body2" color="text.secondary" sx={{ fontSize: compact ? 12 : undefined }}>
                                {subtitle}
                            </Typography>
                            <Typography
                                sx={{
                                    color: amountColor,
                                    fontWeight: 900,
                                    fontSize: compact ? 13 : { xs: 14, sm: 16 },
                                    lineHeight: 1.2,
                                    textAlign: "right",
                                    whiteSpace: "nowrap",
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {formatSignedCurrencyAmountOrMasked(pool.currentBalance, displayCurrency, balancesVisible, locale)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </ButtonBase>
            <IconButton
                size="small"
                onClick={onMenuClick}
                aria-label={menuLabel}
                disabled={menuDisabled}
                title={menuTitle}
                sx={{
                    alignSelf: "center",
                    justifySelf: "center",
                    color: "text.secondary",
                    width: 34,
                    height: 34,
                }}
            >
                <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}
